import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Group, Modal, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import {
  ApiError,
  getCustomersControllerListQueryKey,
  useCustomersControllerCreate,
  type CustomerResponseDtoOutput,
} from '@werkbank/shared';

import { EditDialogFooter } from './MasterDataDialogs';
import { PlzVorschlag } from './PlzVorschlag';

/**
 * Schnellanlage eines Kunden, ohne die Auftragsanlage zu verlassen (Faruq
 * 2026-09, aus der Sage-Übernahme): steht der Auftraggeber noch nicht im
 * Stamm, war das Büro bisher gezwungen, den halb ausgefüllten Auftrag zu
 * verwerfen, in die Kundenverwaltung zu wechseln und von vorn anzufangen.
 *
 * Bewusst dasselbe Muster wie die Einheiten-Schnellanlage: Pflichtminimum
 * (Name + Rechnungsanschrift, weil der Beleg sie braucht), alles andere ist
 * in der Kundenakte nachpflegbar. Nach der Anlage meldet `onCreated` den
 * neuen Kunden zurück, damit der Aufrufer ihn sofort auswählen kann.
 */

const LEER = { displayName: '', street: '', zip: '', city: '', phone: '', email: '' };

export interface CustomerQuickCreateDialogProps {
  opened: boolean;
  onClose: () => void;
  /** Vorbelegung des Namens — z. B. der Suchbegriff, der nichts traf. */
  initialName?: string;
  /** Vorbelegung der Anschrift, etwa aus einer erkannten Auftragsadresse. */
  initialAddress?: { street?: string; zip?: string; city?: string };
  /** Meldung nach erfolgreicher Anlage — Aufrufer selektiert den neuen Kunden. */
  onCreated?: (customer: CustomerResponseDtoOutput) => void;
}

export function CustomerQuickCreateDialog({
  opened,
  onClose,
  initialName,
  initialAddress,
  onCreated,
}: CustomerQuickCreateDialogProps) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<'COMPANY' | 'PRIVATE'>('COMPANY');
  const [werte, setWerte] = useState(LEER);
  const [error, setError] = useState<string | null>(null);

  const create = useCustomersControllerCreate<ApiError>();

  useEffect(() => {
    if (!opened) return;
    setKind('COMPANY');
    setWerte({
      ...LEER,
      displayName: initialName?.trim() ?? '',
      street: initialAddress?.street ?? '',
      zip: initialAddress?.zip ?? '',
      city: initialAddress?.city ?? '',
    });
    setError(null);
  }, [opened, initialName, initialAddress]);

  // Der Beleg braucht eine vollständige Rechnungsanschrift — deshalb ist sie
  // hier Pflicht und nicht erst in der Kundenakte.
  const canSubmit =
    werte.displayName.trim().length > 0 &&
    werte.street.trim().length > 0 &&
    werte.zip.trim().length > 0 &&
    werte.city.trim().length > 0;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setError(null);
    try {
      const neu = await create.mutateAsync({
        data: {
          kind,
          displayName: werte.displayName.trim(),
          billingAddress: {
            street: werte.street.trim(),
            zip: werte.zip.trim(),
            city: werte.city.trim(),
            countryCode: 'DE',
          },
          phone: werte.phone.trim() || undefined,
          email: werte.email.trim() || undefined,
        },
      });
      await queryClient.invalidateQueries({ queryKey: getCustomersControllerListQueryKey() });
      onCreated?.(neu);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Anlage fehlgeschlagen.');
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Neuer Kunde" size="md">
      <Stack gap="sm">
        <SegmentedControl
          fullWidth
          value={kind}
          onChange={(v) => setKind(v as 'COMPANY' | 'PRIVATE')}
          data={[
            { value: 'COMPANY', label: 'Firma' },
            { value: 'PRIVATE', label: 'Privat' },
          ]}
          data-testid="customer-quickcreate-kind"
        />
        <TextInput
          label="Name"
          required
          placeholder={kind === 'COMPANY' ? 'z. B. Hausverwaltung Seebold' : 'z. B. Petri'}
          value={werte.displayName}
          onChange={(e) => setWerte({ ...werte, displayName: e.currentTarget.value })}
          data-testid="customer-quickcreate-name"
        />
        <TextInput
          label="Straße"
          required
          value={werte.street}
          onChange={(e) => setWerte({ ...werte, street: e.currentTarget.value })}
          data-testid="customer-quickcreate-street"
        />
        <Group grow>
          <TextInput
            label="PLZ"
            required
            value={werte.zip}
            onChange={(e) => setWerte({ ...werte, zip: e.currentTarget.value })}
            data-testid="customer-quickcreate-zip"
          />
          <TextInput
            label="Ort"
            required
            value={werte.city}
            onChange={(e) => setWerte({ ...werte, city: e.currentTarget.value })}
            data-testid="customer-quickcreate-city"
          />
        </Group>
        <PlzVorschlag
          street={werte.street}
          city={werte.city}
          zip={werte.zip}
          onUebernehmen={(v) => setWerte({ ...werte, ...v })}
        />
        <Group grow>
          <TextInput
            label="Telefon (optional)"
            value={werte.phone}
            onChange={(e) => setWerte({ ...werte, phone: e.currentTarget.value })}
            data-testid="customer-quickcreate-phone"
          />
          <TextInput
            label="E-Mail (optional)"
            value={werte.email}
            onChange={(e) => setWerte({ ...werte, email: e.currentTarget.value })}
            data-testid="customer-quickcreate-email"
          />
        </Group>
        <Text size="xs" c="dimmed">
          Zahlungsziel, Referenzpflichten und Rechnungsweg stehen auf der Vorgabe der
          Einstellungen und lassen sich in der Kundenakte nachziehen.
        </Text>

        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}
        <EditDialogFooter
          error={null}
          onCancel={onClose}
          onSave={() => void submit()}
          saving={create.isPending}
          saveTestId="customer-quickcreate-submit"
          saveLabel="Kunde anlegen"
          saveDisabled={!canSubmit}
        />
      </Stack>
    </Modal>
  );
}
